/**
 * Calmont Warenkorb — was clientseitig übrig bleibt, seit Horizon die Inhalte
 * selbst rendert ([CAL] Hooks, siehe HORIZON-PATCHES.md):
 *
 *   1. „+" in „Passt dazu" und im leeren Warenkorb
 *      ([data-cal-cart-add="<variantId>"]): POST /cart/add.js mit den
 *      Warenkorb-Sections, dann CartLinesUpdateEvent mit Promise — Horizon
 *      morpht den Drawer mit dem frisch gerenderten Markup selbst (inkl.
 *      unserer Einsätze). Kein zweiter Abruf.
 *   1b. Set-Angebot in der Zeile ([data-cal-cset], snippets/cal-cart-set.liquid):
 *      Chip wählt die Set-Variante, „Zum Set wechseln“ entfernt die Zeile
 *      (/cart/change.js) und fügt das Set in gleicher Menge hinzu.
 *   2. Abgedunkelter Hintergrund am Desktop: Horizon öffnet den Drawer dort
 *      nicht-modal ohne Backdrop und schiebt die Seite zur Seite. Die Fläche
 *      .cal-cart-scrim liegt über der Seite (CSS in assets/cal-cart.css), ein
 *      Klick darauf schließt den Drawer. Mobil übernimmt Horizons ::backdrop.
 */

const ADD = '[data-cal-cart-add]';
const root = () => window.Shopify?.routes?.root ?? '/';

/* Positionen hinzufügen und Horizons Drawer per CartLinesUpdateEvent neu zeichnen lassen. */
async function addItems(trigger, items, quiet) {
  // quiet: Hintergrund-Änderung (Geschenk) – nie die Seite neu laden, sonst springt sie beim Öffnen nach oben
  window.__calAdding = true; setTimeout(() => { window.__calAdding = false; }, 8000);
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
  const body = { items };
  if (sectionIds.length) body.sections = [...new Set(sectionIds)].join(',');
  let CartLinesUpdateEvent = null;
  let deferred = null;
  try {
    ({ CartLinesUpdateEvent } = await import('@shopify/events'));
    deferred = CartLinesUpdateEvent.createPromise();
    trigger.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'cart',
      lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
      promise: deferred.promise,
    }));
  } catch (e) { /* ohne Ereignis-Modul: danach neu laden */ }
  try {
    const res = await fetch(window.Theme?.routes?.cart_add_url || `${root()}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (data.status) throw new Error(data.description || data.message || 'Add failed');
    if (deferred && CartLinesUpdateEvent) {
      const cart = await fetch(`${root()}cart.js`).then((r) => r.json());
      window.__calAdding = false; setTimeout(() => calGift(cart), 0);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'cal-cart', sections: data.sections, didError: false },
      });
    } else if (!quiet) {
      window.location.reload();
    }
  } catch (err) {
    window.__calAdding = false;
    deferred?.reject?.(err);
    throw err;
  }
}

/* Position ändern/entfernen ohne Neuladen: wie addItems über /cart/change.js mit den Warenkorb-Sections
   und CartLinesUpdateEvent – Horizon zeichnet den Drawer selbst neu. */
async function changeItem(trigger, key, quantity, quiet) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
  const body = { id: key, quantity };
  if (sectionIds.length) body.sections = [...new Set(sectionIds)].join(',');
  let Ev = null; let deferred = null;
  try {
    ({ CartLinesUpdateEvent: Ev } = await import('@shopify/events'));
    deferred = Ev.createPromise();
    trigger.dispatchEvent(new Ev({ action: 'change', context: 'cart', lines: [], promise: deferred.promise }));
  } catch (e) { /* ohne Ereignis-Modul: unten neu laden */ }
  window.__calAdding = true;
  try {
    const res = await fetch(`${root()}cart/change.js`, { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const data = await res.json();
    if (data.status) throw new Error(data.description || 'change failed');
    if (deferred && Ev) {
      deferred.resolve({ cart: Ev.createCartFromAjaxResponse(data), detail: { items: data.items, source: 'cal-cart', sections: data.sections, didError: false } });
      return data;
    }
    if (!quiet) window.location.reload();
    return data;
  } catch (err) {
    deferred?.reject?.(err);
    if (!quiet) window.location.reload();
  } finally { window.__calAdding = false; }
  return null;
}

/* Set-Angebot in der Zeile (snippets/cal-cart-set.liquid) */
function csetShow(box) {
  const on = box.querySelector('[data-cal-cset-v][aria-checked="true"]');
  const p = box.querySelector('[data-cal-cset-p]'); const c = box.querySelector('[data-cal-cset-c]');
  const go = box.querySelector('[data-cal-cset-go]');
  if (p) p.textContent = on ? on.dataset.price : '';
  if (c) { c.textContent = on?.dataset.compare || ''; c.hidden = !on?.dataset.compare; }
  if (go) go.disabled = !on;
}

async function csetSwap(button) {
  const box = button.closest('[data-cal-cset]');
  const on = box?.querySelector('[data-cal-cset-v][aria-checked="true"]');
  if (!on || button.classList.contains('is-loading')) return;
  const msg = box.querySelector('[data-cal-cset-msg]'); if (msg) msg.hidden = true;
  button.classList.add('is-loading'); button.setAttribute('aria-busy', 'true');
  const qty = Number(box.dataset.qty) || 1;
  try {
    const r = await fetch(`${root()}cart/change.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: box.dataset.key, quantity: 0 }),
    });
    if (!r.ok) throw new Error('change failed');
    try {
      await addItems(button, [{ id: Number(on.dataset.calCsetV), quantity: qty, properties: { _quelle: 'warenkorb-set' } }]);
    } catch (e) {
      // Set ließ sich nicht hinzufügen: ursprüngliche Zeile wiederherstellen
      const back = Number(box.dataset.variant);
      if (back) await addItems(button, [{ id: back, quantity: qty }]).catch(() => window.location.reload());
      throw e;
    }
  } catch (err) {
    if (msg) { msg.textContent = 'Wechsel gerade nicht möglich – bitte versuch es noch einmal.'; msg.hidden = false; }
    button.classList.remove('is-loading'); button.removeAttribute('aria-busy');
  }
}

async function addVariant(button) {
  if (button.classList.contains('is-loading') || button.disabled) return;
  const id = button.getAttribute('data-cal-cart-add');
  const box = button.closest('.cal-cart-upsell, .cal-cart-empty');
  const error = box?.querySelector('[data-cal-cart-error]');
  if (error) error.hidden = true;
  button.classList.add('is-loading');
  button.setAttribute('aria-busy', 'true');

  const done = () => {
    button.classList.remove('is-loading');
    button.removeAttribute('aria-busy');
  };
  try {
    await addItems(button, [{ id: Number(id), quantity: 1, properties: { _quelle: button.closest('.cal-cart-empty') ? 'warenkorb-leer' : 'warenkorb-empfehlung' } }]);
    // Normalerweise ersetzt der Morph den Knopf; sonst Ladezustand lösen.
    setTimeout(done, 4000);
  } catch (err) {
    if (error) {
      error.textContent = 'Das hat leider nicht geklappt – bitte versuch es noch einmal.';
      error.hidden = false;
    }
    done();
  }
}

if (!window.__calCart) {
  window.__calCart = true;

  // Varianten-Auswahl in den „Passt dazu“-Karten (snippets/cal-cart-card.liquid)
  document.addEventListener('change', (event) => {
    const sel = event.target instanceof Element ? event.target.closest('[data-cal-cv-o]') : null;
    if (!sel) return;
    const box = sel.closest('[data-cal-cv]'); const card = box.closest('.cal-cart-card');
    let vars = []; try { vars = JSON.parse(box.querySelector('[data-cal-cv-json]').textContent); } catch (e) { return; }
    const want = [...box.querySelectorAll('[data-cal-cv-o]')].map((x) => x.value);
    const v = vars.find((x) => x.o.every((y, i) => y === want[i]));
    const btn = card.querySelector('.cal-cart-card__add');
    const pr = card.querySelector('[data-cal-cv-price]');
    if (pr) pr.innerHTML = v ? v.p + (v.c ? ' <s>' + v.c + '</s>' : '') : '';
    if (btn) {
      if (v && v.a) { btn.setAttribute('data-cal-cart-add', v.id); btn.disabled = false; btn.removeAttribute('title'); }
      else { btn.disabled = true; btn.title = v ? 'In dieser Auswahl ausverkauft' : 'Diese Kombination gibt es nicht'; }
    }
  });

  document.addEventListener('click', (event) => {
    const t = event.target instanceof Element ? event.target : null;
    const chip = t?.closest('[data-cal-cset-v]');
    if (chip) {
      event.preventDefault();
      const box = chip.closest('[data-cal-cset]');
      box.querySelectorAll('[data-cal-cset-v]').forEach((c) => c.setAttribute('aria-checked', c === chip ? 'true' : 'false'));
      csetShow(box);
      return;
    }
    const go = t?.closest('[data-cal-cset-go]');
    if (go) { event.preventDefault(); csetSwap(go); return; }
  });

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest(ADD) : null;
    if (!button) return;
    event.preventDefault();
    addVariant(button);
  });

  const scrim = document.createElement('div');
  scrim.className = 'cal-cart-scrim';
  scrim.setAttribute('aria-hidden', 'true');
  scrim.addEventListener('click', () => {
    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.close === 'function') drawer.close();
  });
  document.body.append(scrim);
}


/* Warenkorb-Geschenk (Theme-Einstellungen „Warenkorb-Geschenk“, window.CalGift aus snippets/cal-head.liquid):
   nach jeder Warenkorb-Änderung sofort prüfen, ab Bestellwert hinzufügen, darunter wieder entfernen. */
let calGiftBusy = false;
async function calGift(pre) {
  const G = window.CalGift;
  if (!G || !G.variant || calGiftBusy) return;
  calGiftBusy = true;
  try {
    // pre: Warenkorb, den der Aufrufer gerade schon geladen hat (spart eine zweite, gleichzeitige Abfrage)
    const cart = pre && pre.items ? pre : await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const gl = cart.items.find((i) => i.product_id === G.product);
    const sub = cart.total_price - (gl ? gl.final_line_price : 0);
    window.CalCartHandles = cart.items.map((i) => i.handle); window.CalCartSub = sub; window.CalCartLines = cart.items.filter((i) => i !== gl).map((i) => [i.variant_id, i.final_line_price]); document.dispatchEvent(new CustomEvent('cal:cart-sub', { detail: { sub } }));
    if (sub >= G.threshold && !gl && cart.item_count > 0) {
      await addItems(document.body, [{ id: G.variant, quantity: 1, properties: { _Geschenk: '1' } }], true);
    } else if (sub < G.threshold && gl && gl.properties && gl.properties._Geschenk) {
      // ohne Neuladen entfernen; Summen für Kaufbox-Hinweise aktualisieren
      const after = await changeItem(document.body, gl.key, 0, true);
      if (after) { window.CalCartHandles = after.items.map((i) => i.handle); window.CalCartLines = after.items.map((i) => [i.variant_id, i.final_line_price]); }
    }
  } catch (e) { /* still */ }
  calGiftBusy = false;
}
// Kaufbox (cal-buy) und Kassen-Popup laden den Warenkorb nach dem Hinzufügen ohnehin und reichen ihn hier herein
window.calCartAdd = addItems; // Kassen-/Warenkorb-Popups (assets/cal-xu.js): hinzufügen und Drawer neu zeichnen
window.calGiftCheck = (cart) => (calGiftBusy ? Promise.resolve() : calGift(cart));
// Jeden Warenkorb-Aufruf mitlesen (Kaufbox, Drawer, Mengen) und danach sofort prüfen
const calFetch = window.fetch.bind(window);
window.fetch = async (...args) => {
  const res = await calFetch(...args);
  try {
    const u = String(args[0]?.url || args[0] || '');
    if (/\/cart\/(add|change|update|clear)(\.js)?/.test(u) && !calGiftBusy && !window.__calAdding) setTimeout(calGift, 0);
  } catch (e) { /* still */ }
  return res;
};
document.addEventListener('DOMContentLoaded', () => setTimeout(calGift, 300));
